import { useState } from "react";
import { ListMusic, Camera } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, SectionHead } from "@/components/Reveal";
import { StreamingLinks } from "@/components/StreamingLinks";
import { ALBUMS } from "@/data";

const AlbumCard = ({ album, index }) => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);

  const streamLinks = ["spotify", "apple", "amazon", "youtube"].filter((p) => album.links[p]);

  return (
    <Reveal delay={(index % 3) * 0.1} className="h-full">
      <article
        data-testid={`album-card-${album.id}`}
        className="group relative h-full flex flex-col bg-card2 border border-ivory/10 hover:border-gold/50 transition-colors duration-500 overflow-hidden cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="relative aspect-square overflow-hidden">
          {album.cover ? (
            <>
              <img
                src={album.cover}
                alt={`${album.title} Album Cover`}
                data-testid={`album-cover-${album.id}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-[#0e0e10]">
              <span className="absolute top-3 left-4 gc-eyebrow !text-gold/50">N° {album.photo}</span>
              <Camera className="w-6 h-6 text-gold/40 mb-4" strokeWidth={1.25} />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/60">[ {t.photo} {album.photo} ]</span>
              <span className="mt-2 text-xs text-dim max-w-[220px]">Album Cover · {album.title}</span>
              <span className="mt-1 text-[10px] text-dim/60 italic">{t.photoHint}</span>
            </div>
          )}

          <div
            className={`absolute inset-0 bg-ink/95 backdrop-blur-sm p-6 flex flex-col transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
              lg:translate-y-full lg:group-hover:translate-y-0 lg:opacity-0 lg:group-hover:opacity-100
              ${open ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 lg:opacity-0"}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="gc-eyebrow !text-[9px]">{t.albums.tracklist}</p>
                <h3 className="font-serif text-xl text-ivory mt-1">{album.title}</h3>
              </div>
              <span className="text-xs text-dim">{album.year}</span>
            </div>
            <ol className="mt-4 space-y-1.5 overflow-y-auto flex-1 pr-1">
              {album.tracks.length === 0 ? (
                <li className="text-sm text-dim italic">{t.albums.tracklistPending}</li>
              ) : (
                album.tracks.map((track, i) => (
                  <li key={track} className="flex gap-3 text-sm text-ash font-light">
                    <span className="text-gold/50 font-mono text-[10px] pt-1">{String(i + 1).padStart(2, "0")}</span>
                    {track}
                  </li>
                ))
              )}
            </ol>
            <div className="pt-3 border-t border-ivory/10" onClick={(e) => e.stopPropagation()}>
              <StreamingLinks ids={streamLinks.length ? streamLinks : ["spotify", "apple", "youtube"]} urls={album.links} variant="compact" />
            </div>
          </div>
        </div>

        <div className="p-5 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-serif text-lg text-ivory">{album.title}</h3>
              <p className="mt-1 text-[11px] tracking-[0.15em] uppercase text-dim">{album.type[lang] ?? album.type.en} · {album.year}</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-dim font-light leading-relaxed">{album.desc[lang] ?? album.desc.en}</p>

          <div className="mt-auto pt-5 flex items-center justify-end gap-3">
            <button
              data-testid={`tracklist-toggle-${album.id}`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen((v) => !v);
              }}
              className={`p-2.5 border transition-colors duration-300 ${open ? "border-gold text-gold" : "border-ivory/20 text-ash hover:border-gold hover:text-gold"}`}
              aria-label={t.albums.tracklist}
            >
              <ListMusic className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export const Albums = () => {
  const { t } = useLanguage();
  return (
    <section id="albums" data-testid="albums-section" className="py-24 lg:py-36 bg-coal/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead title={t.albums.title} sub={t.albums.sub} testId="albums-title" />
        <div id="music" className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {ALBUMS.map((album, i) => (
            <AlbumCard key={album.id} album={album} index={i} />
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="text-xs text-dim/70 italic">{t.albums.sampleNote}</p>
        </Reveal>
      </div>
    </section>
  );
};
